/* Types */
import { CellContext, Row } from '@tanstack/react-table';
import { ClientLink } from 'application/Shared/types/clientLink';
import { ConnectionDetailsType } from 'application/Shared/types/tenantProvider';

/*  React  */
import { useTranslation } from 'react-i18next';

import { ColumnFilter } from '../Table/types';

/**
 * Configuration object for integration status filtering control.
 */
export const integrationStatusFilter: ColumnFilter<unknown> = {
    control: 'Checkbox',
    defaultOptions: [
        {
            options: [
                'PartnerOrganization.integrationStatus.connected',
                'PartnerOrganization.integrationStatus.disconnected',
                'PartnerOrganization.integrationStatus.noIntegration',
                'PartnerOrganization.integrationStatus.updateRequired'
            ]
        }
    ]
};

/**
 * Filters data based on provider.connectionDetails.
 *
 * @template TData - The type of data being filtered
 * @param {Row<TData>} row - The row of data to check
 * @param {string} columnId - The ID of the column containing the date
 * @param {string} filterValue - The filter value (e.g., 'Authenticated', 'Disconnected'.)
 *
 * @returns {boolean} - True if the provider.connectionDetails meets a criteria, false otherwise
 *
 * @example
 * // Returns true if the provider.connectionDetails in the row is null or undefined
 * integrationStatusFilterFn(row, 'provider.connectionDetails', 'Not integrated')
 */
export function integrationStatusFilterFn<TData>(
    row: Row<TData>,
    columnId: string,
    filterValue: string
): boolean {
    if (!filterValue) return true;

    const filterValueArray = Array.isArray(filterValue)
        ? filterValue
        : [filterValue];

    if (filterValueArray.length === 0) return true;

    const connectionDetails = row.getValue(columnId) as ConnectionDetailsType;

    if (!connectionDetails)
        return filterValueArray.includes(
            'PartnerOrganization.integrationStatus.noIntegration'
        );
    if (connectionDetails.isUpdateRequired)
        return filterValueArray.includes(
            'PartnerOrganization.integrationStatus.updateRequired'
        );
    if (connectionDetails.googleEnabled || connectionDetails.azureEnabled)
        return filterValueArray.includes(
            'PartnerOrganization.integrationStatus.connected'
        );
    if (connectionDetails)
        return filterValueArray.includes(
            'PartnerOrganization.integrationStatus.disconnected'
        );
    return false;
}

/**
 * Helper function that determines the integration status based on connection details.
 * 1. If provider identity type is not set, there is no integration
 * 2. If update is required, there is an integration update required
 * 3. If google or Microsoft Entra ID is enabled, there is an integration authenticated
 * 4. If provider identity type is set and no google or Microsoft Entra ID is enabled,
 * there was an integration added but later disconnected
 * @param connectionDetails - The connection details from the provider
 * @param t - Translation function for localizing status messages
 * @returns {string} - The integration status localized string
 */
export const getIntegrationStatus = (
    connectionDetails: ConnectionDetailsType,
    t: (key: string) => string
): string | null => {
    if (!connectionDetails) {
        return t('PartnerOrganization.integrationStatus.noIntegration');
    }

    if (connectionDetails.isUpdateRequired) {
        return t('PartnerOrganization.integrationStatus.updateRequired');
    }

    if (connectionDetails.googleEnabled || connectionDetails.azureEnabled) {
        // Integration is authenticated
        return t('PartnerOrganization.integrationStatus.connected');
    }

    // Integration has been added.
    if (connectionDetails) {
        return t('PartnerOrganization.integrationStatus.disconnected');
    }

    return null;
};

/**
 * React component that renders the integration status for a partner organization.
 * @param cell - The cell context containing the connection details
 * @returns {string | null} - The localized integration status string or null
 */
export const IntegrationStatusCell = ({
    cell
}: CellContext<ClientLink, ConnectionDetailsType>): string | null => {
    const { t } = useTranslation();

    const connectionDetails = cell.getValue();

    return getIntegrationStatus(connectionDetails, t);
};
